import React, { useEffect, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { V2FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { V2TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { CLIA_PRACTICES } from '@src/state/tinRegistration/utils';
import { STATE_LIST } from '@src/common/stateOptions';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';
import practiceTypes from './practiceTypes.json';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const CardTitle = styled('h2', {
  margin: 0,
  color: '$primary1',
});

const FormRows = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
});

const TwoColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  width: '100%',
});

const SingleColumn = styled('div', {
  width: '100%',
});

const FullWidthColumn = styled('div', {
  width: '100%',
});

const LicenseRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '12px',
  alignItems: 'start',
  width: '100%',
});

const CharacterCount = styled('span', {
  fontSize: '12px',
  color: '$gray6',
});

const TextInputAreaStyles = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$web.semantic.spacing.scale.sm',
  flexGrow: 0,
  '.abyss-text-input-area': {
    height: '78px',
  },
});

const CheckboxRow = styled('div', {
  marginTop: '12px',
});

const FILE_UPLOAD_CONTENT_WIDTH_CSS = { width: '100%', maxWidth: '380px' };
const FILE_UPLOAD_CSS = {
  'abyss-file-upload-drop-zone': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-input-file-list-list': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-upload-file-list-item': { wordBreak: 'break-word' },
};
const DOC360_UPLOAD_ERROR_ALERT_CSS = {
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
};
const ATTESTATION_CHECKBOX_CSS = {
  'abyss-checkbox-root': { width: '100%' },
  'abyss-checkbox-label': { maxWidth: 'none', width: '100%' },
};

const FileRow = styled('div', {
  width: '100%',
  maxWidth: '460px',
  border: '1px solid $core.color.neutral.40',
  borderRadius: '8px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  backgroundColor: '$white',
});

const FileName = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$core.color.neutral.90',
  maxWidth: '460px',
  wordBreak: 'break-word',
});

const RemoveFileButton = styled('button', {
  border: 'none',
  background: 'transparent',
  color: '$core.color.neutral.70',
  width: '20px',
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  borderRadius: '2px',
  '&:hover': {
    color: '$core.color.neutral.90',
  },
});

const MAX_FILE_NAME_LENGTH = 54; // 50 chars + 4 for .pdf extension
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const PASSWORD_PROTECTED_UPLOAD_MESSAGE =
  'Upload failed: This file is password-protected and cannot be processed. Please remove the password and try uploading again.';
const DOC360_UPLOAD_ERROR_MESSAGE =
  "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)";

const MAX_LICENSE = 5;

const normalizeNpiValue = (value = '') => {
  let updatedVal = value.replace(/[!@#$%^&*)({}<>.[\]\-+_`~=\\|';":/?]/g, '');
  updatedVal = updatedVal.replace(/\s/g, ',').replace(/,,/g, ',');
  return updatedVal;
};

const parseNpiList = (value = '') =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const isValidNpiList = (value = '') => {
  if (!value) return true;
  if (value.length > 549) return false;
  const normalizedValue = value.replace(/\s/g, '');
  if (normalizedValue.match(/[^\w,]/)) return false;
  return parseNpiList(value).every((npi) => npi.length === 10);
};

const getFileList = (files) => {
  if (!files) return [];
  return Array.isArray(files) ? files : [files];
};

const FILE_UPLOAD_VALIDATE = {
  maxFiles: (files) => {
    const normalizedFiles = getFileList(files);
    return normalizedFiles.length > 1 ? 'Maximum 1 file allowed.' : true;
  },
  fileLength: (files) => {
    const normalizedFiles = getFileList(files);
    return normalizedFiles.some(
      (file) => file?.name?.length > MAX_FILE_NAME_LENGTH
    )
      ? 'File Name cannot exceed 50 characters in length.'
      : true;
  },
  fileSize: (files) => {
    const normalizedFiles = getFileList(files);
    return normalizedFiles.some((file) => file?.size > MAX_FILE_SIZE_BYTES)
      ? 'File is larger than 5 MB.'
      : true;
  },
};

const getUploadErrorMessage = (error) => {
  const messageCandidates = [
    error?.message,
    error?.data?.message,
    error?.response?.data?.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const faultCodeCandidates = [
    error?.faultCode,
    error?.data?.faultCode,
    error?.response?.data?.faultCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const isPasswordProtectedError =
    /password[ -]?protected|encrypted|cannot be processed/.test(
      messageCandidates
    ) ||
    /password|encrypt/.test(faultCodeCandidates) ||
    faultCodeCandidates.includes('doduee');

  if (isPasswordProtectedError) {
    return PASSWORD_PROTECTED_UPLOAD_MESSAGE;
  }

  return error?.message || DOC360_UPLOAD_ERROR_MESSAGE;
};

export const PracticeInformation = () => {
  const form = useFormV2();
  const userInfo = useUser.state();
  const { trackingNumber, documentIds } = useTinRegistration.state();
  const { uploadProof, setDocumentId, setDocumentLoading } =
    useTinRegistration.actions();
  const [doc360UploadError, setDoc360UploadError] = useState('');
  const [visibleFileName, setVisibleFileName] = useState('');
  const [showRestoredFileRow, setShowRestoredFileRow] = useState(false);

  const practiceType = form.watch('practiceInformation.practiceTypeSelection');
  const restrictionSelection = form.watch(
    'practiceInformation.restrictionSelection'
  );
  const npiValue = form.watch('practiceInformation.npiInformation') || '';
  const cliaIdNumber =
    form.watch('practiceInformation.cliaIdentification') || '';
  const uploadSuccess = documentIds?.['practiceInformation.cliaFile'];
  const cmsCheckboxDisabled = !!cliaIdNumber || !!uploadSuccess;
  const medicareId = form.watch('practiceInformation.medicareId') || '';
  const licenses = form.watch('practiceInformation.medLicense') || [];

  useEffect(() => {
    const currentLicenses = form.getValues('practiceInformation.medLicense');
    if (!Array.isArray(currentLicenses) || currentLicenses.length === 0) {
      form.setValue('practiceInformation.medLicense', [
        { licenseType: '', licenseNumber: '', state: '' },
      ]);
    }
  }, []);

  useEffect(() => {
    if (restrictionSelection !== 'Yes') {
      form.setValue('practiceInformation.restrictionInput', '', {
        touched: false,
        errors: { required: false },
      });
    }
  }, [restrictionSelection]);

  useEffect(() => {
    const value = form.getValues('practiceInformation.cliaFile');
    const file = Array.isArray(value) ? value[0] : value;
    const nextFileName = file?.name || '';
    const hasRestoredDraftFile =
      !!nextFileName && !!documentIds['practiceInformation.cliaFile'];
    setVisibleFileName(hasRestoredDraftFile ? nextFileName : '');
    setShowRestoredFileRow(hasRestoredDraftFile);
  }, []);

  const handleCliaFileChange = () => {
    const uploadedValue = form.getValues('practiceInformation.cliaFile');
    const uploadedFile = Array.isArray(uploadedValue)
      ? uploadedValue[0]
      : uploadedValue;

    setDoc360UploadError('');

    if (!uploadedFile) {
      setVisibleFileName('');
      setShowRestoredFileRow(false);
      setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
      return;
    }

    // If this is a restored draft file object, preserve its existing doc360Id.
    if (!(uploadedFile instanceof File)) {
      const restoredDocId =
        uploadedFile?.doc360Id || documentIds['practiceInformation.cliaFile'];
      if (uploadedFile && uploadedFile.name && restoredDocId) {
        setVisibleFileName(uploadedFile.name);
        setShowRestoredFileRow(true);
        if (!documentIds['practiceInformation.cliaFile']) {
          setDocumentId({
            id: 'practiceInformation.cliaFile',
            value: restoredDocId,
          });
        }
        form.setValue('practiceInformation.cliaAttestation', false, {
          touched: false,
          errors: { required: false },
        });
        return;
      }
      setDoc360UploadError(DOC360_UPLOAD_ERROR_MESSAGE);
      setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
      return;
    }

    // Clear the doc360Id since this is a new upload
    setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
    // Hide the restored-draft fallback row so Abyss can render the standard file row for fresh uploads.
    setVisibleFileName('');
    setShowRestoredFileRow(false);

    const isInvalidCliaFile =
      uploadedFile.size > MAX_FILE_SIZE_BYTES ||
      uploadedFile.name.length > MAX_FILE_NAME_LENGTH;
    if (isInvalidCliaFile) {
      setVisibleFileName('');
      setShowRestoredFileRow(false);
      setDocumentLoading({
        model: 'practiceInformation.cliaFile',
        value: false,
      });
      return;
    }

    const registration = form.getValues('searchRegistration');
    const reqBody = {
      billingTin: registration?.billingTin,
      requestId: registration?.requestId,
      fileType: 'CMS',
      trackingNumber,
      file: uploadedFile,
      test: userInfo?.isCheckoutUser,
      model: 'practiceInformation.cliaFile',
    };

    setDocumentLoading({ model: 'practiceInformation.cliaFile', value: true });
    uploadProof(
      reqBody,
      (resp) => {
        setDocumentLoading({
          model: 'practiceInformation.cliaFile',
          value: false,
        });
        if (resp?.doc360Id) {
          setDocumentId({
            id: 'practiceInformation.cliaFile',
            value: resp.doc360Id,
          });
          form.setValue('practiceInformation.cliaAttestation', false, {
            shouldValidate: true,
          });
          return;
        }
        setDoc360UploadError(DOC360_UPLOAD_ERROR_MESSAGE);
        form.setValue('practiceInformation.cliaFile', [], {
          touched: false,
          errors: { required: true },
        });
        setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
      },
      (error) => {
        setDocumentLoading({
          model: 'practiceInformation.cliaFile',
          value: false,
        });
        setDoc360UploadError(getUploadErrorMessage(error));
        form.setValue('practiceInformation.cliaFile', [], {
          touched: false,
          errors: { required: true },
        });
        setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
      }
    );
  };

  const handleRemoveVisibleFile = () => {
    form.setValue('practiceInformation.cliaFile', [], {
      touched: false,
      errors: { required: true },
    });
    setVisibleFileName('');
    setShowRestoredFileRow(false);
    setDoc360UploadError('');
    setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
  };

  const addLicense = () => {
    const current = form.getValues('practiceInformation.medLicense') || [];
    if (current.length >= MAX_LICENSE) return;
    form.setValue('practiceInformation.medLicense', [
      ...current,
      { licenseType: '', licenseNumber: '', state: '' },
    ]);
  };

  const removeLicense = (index) => {
    const current = form.getValues('practiceInformation.medLicense') || [];
    if (current.length <= 1) return;
    const next = current.filter((_, idx) => idx !== index);
    form.setValue('practiceInformation.medLicense', next);
  };

  return (
    <V2Card>
      <SectionStack>
        <CardTitle>Practice information</CardTitle>
        <FormRows>
          <SingleColumn>
            <V2RadioGroup
              id="practice-registration-type"
              label="Registration type"
              model="practiceInformation.registrationTypeSelection"
              isRequired
              validators={{ required: 'Please select one.' }}
            >
              <V2RadioGroup.Radio
                id="practice-registration-type-individual"
                value="Individual"
                label="Individual"
              />
              <V2RadioGroup.Radio
                id="practice-registration-type-group"
                value="Group"
                label="Group/Facility"
              />
            </V2RadioGroup>
          </SingleColumn>

          <TwoColumnRow>
            <V2SelectInput
              id="practice-type"
              label="Practice type"
              model="practiceInformation.practiceTypeSelection"
              isSearchable
              width="300px"
              options={practiceTypes}
              placeholder="Select One"
              validators={{ required: 'Please select one.' }}
            />

            {practiceType && CLIA_PRACTICES.includes(practiceType) && (
              <V2TextInput
                id="practice-clia-id"
                label="CLIA Identification Number (optional)"
                model="practiceInformation.cliaIdentification"
                width="335px"
                formatter={(value) => {
                  return value.replace(
                    /[!@#$%^&*)({}<>,.[\]+_`~=\\|';":?]/g,
                    ''
                  );
                }}
                maxLength={10}
                isClearable
                onChange={() =>
                  form.setValue('practiceInformation.cliaAttestation', false, {
                    shouldValidate: true,
                  })
                }
              />
            )}
          </TwoColumnRow>

          {practiceType && CLIA_PRACTICES.includes(practiceType) && (
            <SingleColumn>
              <V2FileUpload
                id="upload-clia-input"
                model="practiceInformation.cliaFile"
                label="CMS 116"
                subText="File must not be password-protected."
                css={FILE_UPLOAD_CSS}
                variant="minimal"
                fileTypes={{ 'application/pdf': ['.pdf'] }}
                onChange={handleCliaFileChange}
                validators={{
                  validate: FILE_UPLOAD_VALIDATE,
                }}
              />

              {showRestoredFileRow && visibleFileName && (
                <FileRow>
                  <FileName>
                    <span>{visibleFileName}</span>
                  </FileName>
                  <RemoveFileButton
                    type="button"
                    onClick={handleRemoveVisibleFile}
                  >
                    <IconSymbol icon="close" variant="outlined" size="16px" />
                  </RemoveFileButton>
                </FileRow>
              )}

              {doc360UploadError && (
                <V2Alert
                  variant="error"
                  dismissible={false}
                  inline
                  css={DOC360_UPLOAD_ERROR_ALERT_CSS}
                >
                  {doc360UploadError}
                </V2Alert>
              )}

              <CheckboxRow>
                <V2Checkbox
                  id="practice-clia-attestation"
                  model="practiceInformation.cliaAttestation"
                  label="By not attaching CMS 116 and/or inputting CLIA ID above, I attest that it is not applicable for my practice or is pending issuance from CMS. I understand that my TIN registration may take longer to verify or may be rejected."
                  isDisabled={cmsCheckboxDisabled}
                  validators={{
                    validate: (value) => {
                      const cliaId =
                        form.getValues(
                          'practiceInformation.cliaIdentification'
                        ) || '';
                      const cliaFile = form.getValues(
                        'practiceInformation.cliaFile'
                      );
                      const hasFile = Array.isArray(cliaFile)
                        ? cliaFile.some(Boolean)
                        : !!cliaFile;
                      if (cliaId || hasFile) return null;
                      return !value
                        ? 'Must check box before proceeding.'
                        : null;
                    },
                  }}
                  css={ATTESTATION_CHECKBOX_CSS}
                />
              </CheckboxRow>
            </SingleColumn>
          )}

          {practiceType === 'Ambulatory Surgery Center' && (
            <SingleColumn>
              <V2TextInput
                id="practice-medicare-id"
                label="Medicare ID (optional)"
                width="335px"
                model="practiceInformation.medicareId"
                formatter={(value) => {
                  return value.replace(
                    /[!@#$%^&*)({}<>,.[\]+_`~=\\|';":?]/g,
                    ''
                  );
                }}
                maxLength={11}
                isClearable
                onChange={() =>
                  form.setValue(
                    'practiceInformation.medicareAttestation',
                    false,
                    { shouldValidate: true }
                  )
                }
              />

              <CheckboxRow>
                <V2Checkbox
                  id="practice-medicare-attestation"
                  model="practiceInformation.medicareAttestation"
                  label="By not inputting Medicare ID above, I attest that it is not applicable for my practice or is pending issuance from CMS. I understand that my registration may take longer to verify or may be rejected."
                  isDisabled={!!medicareId}
                  validators={{
                    validate: (value) => {
                      const medId =
                        form.getValues('practiceInformation.medicareId') || '';
                      if (medId) return null;
                      return !value
                        ? 'Must check box before proceeding.'
                        : null;
                    },
                  }}
                  css={ATTESTATION_CHECKBOX_CSS}
                />
              </CheckboxRow>
            </SingleColumn>
          )}

          <FullWidthColumn>
            <V2RadioGroup
              id="practice-restriction-selection"
              label="Has any individuals in your practice ever had their professional license relinquished, denied, suspended, revoked, restricted, or have you ever been subject to a fine, reprimand, consent order, probation or any conditions or limitations by any state or professional licensing board?"
              model="practiceInformation.restrictionSelection"
              isRequired
              validators={{ required: 'Please select one.' }}
            >
              <V2RadioGroup.Radio
                id="practice-restriction-selection-yes"
                value="Yes"
                label="Yes"
              />
              <V2RadioGroup.Radio
                id="practice-restriction-selection-no"
                value="No"
                label="No"
              />
            </V2RadioGroup>
          </FullWidthColumn>

          {restrictionSelection === 'Yes' && (
            <SingleColumn>
              <TextInputAreaStyles>
                <V2TextInputArea
                  id="practice-restriction-input"
                  label="Please provide additional detail including provider names and license restrictions."
                  model="practiceInformation.restrictionInput"
                  rows={5}
                  maxLength={500}
                  css={{
                    'abyss-text-input-area-character-count': {
                      display: 'none',
                    },
                    'abyss-text-input-area-root': {
                      '& > [data-visually-hidden] + div': {
                        minHeight: 0,
                      },
                    },
                  }}
                  validators={{
                    validate: (value) => {
                      if (restrictionSelection !== 'Yes') return null;
                      return !value?.trim() ? 'Enter a value' : null;
                    },
                  }}
                  autoComplete="off"
                />
                <CharacterCount>
                  {
                    (form.watch('practiceInformation.restrictionInput') || '')
                      .length
                  }
                  /500 Characters
                </CharacterCount>
              </TextInputAreaStyles>
            </SingleColumn>
          )}

          <SectionStack>
            {(licenses || []).map((_, index) => (
              <LicenseRow key={`license-row-${index}`}>
                <V2TextInput
                  id={`practice-license-type-${index}`}
                  label="License Type"
                  model={`practiceInformation.medLicense[${index}].licenseType`}
                  formatter={(value) => {
                    return value.replace(
                      /[!@#$%^&*)({}<> ,[\]+`~=\\|';":/?]/g,
                      ''
                    );
                  }}
                  validators={{ required: 'License type is required.' }}
                  maxLength={20}
                  isClearable
                />

                <V2TextInput
                  id={`practice-license-number-${index}`}
                  label="License Number"
                  model={`practiceInformation.medLicense[${index}].licenseNumber`}
                  formatter={(value) => {
                    return value.replace(
                      /[!@#$%^&*)({}<> ,[\]+`~=\\|';":/?]/g,
                      ''
                    );
                  }}
                  validators={{ required: 'License number is required.' }}
                  maxLength={20}
                  isClearable
                />

                <V2SelectInput
                  id={`practice-license-state-${index}`}
                  label="State"
                  model={`practiceInformation.medLicense[${index}].state`}
                  options={STATE_LIST}
                  isSearchable
                  placeholder="Select One"
                  validators={{ required: 'State is required.' }}
                />

                {licenses.length > 1 && (
                  <V2Button variant="text" onClick={() => removeLicense(index)}>
                    <IconSymbol icon="delete" size="18px" />
                  </V2Button>
                )}
              </LicenseRow>
            ))}

            <V2Button
              variant="text"
              size="$sm"
              icon={{ icon: 'add_circle_outline', position: 'leading' }}
              onClick={addLicense}
              css={{
                'abyss-button-content-container': {
                  justifyContent: 'flex-start',
                },
                'abyss-button-root': {
                  paddingLeft: 0,
                  width: 'fit-content',
                },
              }}
              isDisabled={licenses.length >= MAX_LICENSE}
            >
              Add additional license
            </V2Button>
          </SectionStack>

          <SingleColumn>
            <TextInputAreaStyles>
              <V2TextInputArea
                id="practice-npi-info"
                helper="Provide NPI(s) for all individuals and groups/facilities who will submit UHC claims."
                label="National Provider Identifier (NPI)"
                model="practiceInformation.npiInformation"
                subText={{
                  text: 'Enter your NPI(s) here. Each NPI must be 10 alphanumeric characters. Separate multiple NPIs with commas.',
                  position: 'above',
                }}
                rows={5}
                maxLength={549}
                formatValue={normalizeNpiValue}
                validators={{
                  validate: (value) => {
                    if (!value) return null;

                    const invalidValues = parseNpiList(value).filter(
                      (item) => item.length !== 10
                    );

                    if (invalidValues.length > 0) {
                      return `Invalid NPI values entered: ${invalidValues.join(
                        ','
                      )}`;
                    }

                    if (!isValidNpiList(value)) {
                      return 'Information entered is in an invalid format.';
                    }

                    return null;
                  },
                }}
                autoComplete="off"
              />
              <CharacterCount>{npiValue.length}/549 Characters</CharacterCount>
            </TextInputAreaStyles>
          </SingleColumn>
        </FormRows>
      </SectionStack>
    </V2Card>
  );
};
